import { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  TrendingUp, 
  TrendingDown,
  DollarSign,
  Clock,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  BarChart3
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Chart tick styles (memoized to prevent re-renders)
const CHART_TICK_STYLE = { fontSize: 11, fill: '#7F8C8D' };
const CHART_AXIS_STYLE = { stroke: '#E5E7EB' };
const TOOLTIP_CONTENT_STYLE = { 
  backgroundColor: '#F4F1E8', 
  border: '1px solid #7F8C8D',
  borderRadius: 0
};

// Format currency
const formatCurrency = (value, currency = 'GBP') => {
  if (value === null || value === undefined) return '—';
  const symbol = currency === 'GBP' ? '£' : currency === 'USD' ? '$' : '€';
  return `${symbol}${Math.abs(value).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

// Hero Metric Card (Cash Position & Runway)
const HeroMetric = ({ label, value, subtitle, icon: Icon, trend }) => (
  <div className="bg-white border border-iron-grey p-6 card-shadow" data-testid={`hero-metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-start justify-between mb-2">
      <span className="font-inter text-xs uppercase tracking-wider text-iron-grey">{label}</span>
      {Icon && <Icon size={20} className="text-iron-grey" strokeWidth={1.5} />}
    </div>
    <p className="font-fraunces font-bold text-4xl lg:text-5xl text-obsidian mb-1">
      {value}
    </p>
    {subtitle && (
      <div className="flex items-center gap-2">
        {trend !== null && trend !== undefined && (
          <span className={`flex items-center gap-1 font-inter text-sm font-medium ${trend >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
            {trend >= 0 ? <TrendingUp size={14} strokeWidth={2} /> : <TrendingDown size={14} strokeWidth={2} />}
            {trend >= 0 ? '+' : ''}{trend}%
          </span>
        )}
        <span className="font-inter text-sm text-iron-grey">{subtitle}</span>
      </div>
    )}
  </div>
);

// Stat Card with Mini Chart
const StatCard = ({ label, value, trend, chartData, color = '#3B82F6' }) => (
  <div className="bg-white border border-iron-grey p-4 card-shadow" data-testid={`stat-card-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <span className="font-inter text-xs uppercase tracking-wider text-iron-grey">{label}</span>
    <div className="flex items-end justify-between mt-2">
      <div>
        <p className="font-fraunces font-bold text-2xl text-obsidian">{value}</p>
        {trend !== null && trend !== undefined && (
          <span className={`font-inter text-xs font-medium ${trend >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
            {trend >= 0 ? '▲' : '▼'} {Math.abs(trend)}%
          </span>
        )}
      </div>
      {chartData && chartData.length > 0 && (
        <div className="w-20 h-10">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <Area 
                type="monotone" 
                dataKey="value" 
                stroke={color} 
                fill={color}
                fillOpacity={0.2}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  </div>
);

// Tax Reserve Gauge
const TaxReserveGauge = ({ liability, reserved, percentage }) => {
  const getColor = (pct) => {
    if (pct >= 80) return 'bg-emerald-500';
    if (pct >= 50) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const getStatus = (pct) => {
    if (pct >= 80) return { text: 'On Track', icon: CheckCircle, color: 'text-emerald-600' };
    if (pct >= 50) return { text: 'Attention Needed', icon: AlertTriangle, color: 'text-amber-600' };
    return { text: 'Action Required', icon: AlertTriangle, color: 'text-red-500' };
  };

  const status = percentage !== null ? getStatus(percentage) : null;

  return (
    <div className="bg-white border border-iron-grey p-6 card-shadow" data-testid="tax-reserve-gauge">
      <div className="flex items-start justify-between mb-4">
        <div>
          <span className="font-inter text-xs uppercase tracking-wider text-iron-grey">Tax Reserve</span>
          <p className="font-fraunces font-bold text-xl text-obsidian mt-1">
            {liability ? formatCurrency(reserved) : '—'} / {liability ? formatCurrency(liability) : '—'}
          </p>
        </div>
        {status && (
          <div className={`flex items-center gap-1 ${status.color}`}>
            <status.icon size={16} strokeWidth={2} />
            <span className="font-inter text-xs font-medium">{status.text}</span>
          </div>
        )}
      </div>
      
      {percentage !== null ? (
        <>
          <div className="w-full bg-iron-grey/20 h-4 overflow-hidden">
            <div 
              className={`h-full ${getColor(percentage)} transition-all duration-500 ease-out`}
              style={{ width: `${Math.min(100, percentage)}%` }}
            />
          </div>
          <p className="font-inter text-sm text-iron-grey mt-2">
            {Math.round(percentage)}% of estimated liability reserved
          </p>
        </>
      ) : (
        <p className="font-inter text-sm text-iron-grey">No tax data available</p>
      )}
    </div>
  );
};

// Net Profit vs Burn Rate Chart
const ProfitBurnChart = ({ netProfitHistory, burnRateHistory }) => {
  // Combine data for side-by-side comparison
  const combinedData = [];
  const allPeriods = new Set([
    ...netProfitHistory.map(d => d.period),
    ...burnRateHistory.map(d => d.period)
  ]);

  allPeriods.forEach(period => {
    const profit = netProfitHistory.find(d => d.period === period);
    const burn = burnRateHistory.find(d => d.period === period);
    combinedData.push({
      period,
      profit: profit?.value || 0,
      expenses: burn?.value || 0
    });
  });

  if (combinedData.length === 0) {
    return (
      <div className="bg-white border border-iron-grey p-6 card-shadow" data-testid="profit-burn-chart">
        <span className="font-inter text-xs uppercase tracking-wider text-iron-grey">Net Profit vs Expenses</span>
        <div className="flex items-center justify-center h-48 text-iron-grey">
          <p className="font-inter text-sm">No financial data available yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-iron-grey p-6 card-shadow" data-testid="profit-burn-chart">
      <span className="font-inter text-xs uppercase tracking-wider text-iron-grey mb-4 block">Net Profit vs Expenses</span>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={combinedData} barGap={0} barCategoryGap="20%">
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
            <XAxis 
              dataKey="period" 
              tick={CHART_TICK_STYLE}
              axisLine={CHART_AXIS_STYLE}
            />
            <YAxis 
              tick={CHART_TICK_STYLE}
              axisLine={CHART_AXIS_STYLE}
              tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`}
            />
            <Tooltip 
              formatter={(value) => formatCurrency(value)}
              labelStyle={{ color: '#0A0A0A', fontWeight: 600 }}
              contentStyle={TOOLTIP_CONTENT_STYLE}
            />
            <Bar dataKey="profit" name="Net Profit" radius={[0, 0, 0, 0]}>
              {combinedData.map((entry, index) => (
                <Cell key={`profit-${entry.period}-${index}`} fill={entry.profit >= 0 ? '#10B981' : '#EF4444'} />
              ))}
            </Bar>
            <Bar dataKey="expenses" name="Expenses" fill="#7F8C8D" radius={[0, 0, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center gap-6 mt-4 justify-center">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-emerald-500" />
          <span className="font-inter text-xs text-iron-grey">Net Profit</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-iron-grey" />
          <span className="font-inter text-xs text-iron-grey">Expenses</span>
        </div>
      </div>
    </div>
  );
};

// Revenue Trend Chart (MoM)
const RevenueTrendChart = ({ revenueHistory, momChange }) => {
  if (!revenueHistory || revenueHistory.length === 0) {
    return (
      <div className="bg-white border border-iron-grey p-6 card-shadow" data-testid="revenue-trend-chart">
        <div className="flex items-start justify-between mb-4">
          <span className="font-inter text-xs uppercase tracking-wider text-iron-grey">Revenue Trend</span>
        </div>
        <div className="flex items-center justify-center h-48 text-iron-grey">
          <p className="font-inter text-sm">No revenue data available yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-iron-grey p-6 card-shadow" data-testid="revenue-trend-chart">
      <div className="flex items-start justify-between mb-4">
        <span className="font-inter text-xs uppercase tracking-wider text-iron-grey">Revenue Trend</span>
        {momChange !== null && (
          <span className={`flex items-center gap-1 font-inter text-sm font-medium ${momChange >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
            {momChange >= 0 ? <TrendingUp size={14} strokeWidth={2} /> : <TrendingDown size={14} strokeWidth={2} />}
            {momChange >= 0 ? '+' : ''}{momChange}% from last month
          </span>
        )}
      </div>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={revenueHistory}>
            <defs>
              <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
            <XAxis 
              dataKey="period" 
              tick={{ fontSize: 11, fill: '#7F8C8D' }}
              axisLine={{ stroke: '#E5E7EB' }}
            />
            <YAxis 
              tick={{ fontSize: 11, fill: '#7F8C8D' }}
              axisLine={{ stroke: '#E5E7EB' }}
              tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`}
            />
            <Tooltip 
              formatter={(value) => [formatCurrency(value), 'Revenue']}
              labelStyle={{ color: '#0A0A0A', fontWeight: 600 }}
              contentStyle={{ 
                backgroundColor: '#F4F1E8', 
                border: '1px solid #7F8C8D',
                borderRadius: 0
              }}
            />
            <Area 
              type="monotone" 
              dataKey="value" 
              stroke="#3B82F6" 
              strokeWidth={2}
              fill="url(#revenueGradient)"
              dot={{ fill: '#3B82F6', strokeWidth: 0, r: 4 }}
              activeDot={{ r: 6, stroke: '#3B82F6', strokeWidth: 2, fill: '#F4F1E8' }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// Empty State
const EmptyDashboard = ({ onRefresh, isRefreshing }) => (
  <div className="bg-white border border-iron-grey p-12 card-shadow text-center" data-testid="empty-dashboard">
    <BarChart3 size={64} className="mx-auto mb-6 text-iron-grey" strokeWidth={1} />
    <h2 className="font-fraunces font-bold text-2xl text-obsidian mb-3">
      Your Financial Dashboard
    </h2>
    <p className="font-inter text-iron-grey max-w-md mx-auto mb-6">
      Once your accountant sends back processed financial documents (P&L statements, balance sheets), 
      your dashboard will automatically populate with insights extracted by our AI.
    </p>
    <Button 
      onClick={onRefresh}
      disabled={isRefreshing}
      className="btn-secondary"
      data-testid="refresh-dashboard-btn"
    >
      <RefreshCw size={16} strokeWidth={1.5} className={`mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
      {isRefreshing ? 'Analyzing...' : 'Check for Updates'}
    </Button>
  </div>
);

// Main Dashboard Component
const FinancialDashboard = () => {
  const { getAuthHeader } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dashboard/metrics`, {
        headers: getAuthHeader()
      });
      setMetrics(response.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load dashboard metrics');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/dashboard/refresh`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Documents queued for analysis');
      // Wait a moment then refresh
      setTimeout(() => {
        fetchDashboard();
        setRefreshing(false);
      }, 3000);
    } catch (error) {
      toast.error('Failed to refresh dashboard');
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="font-inter text-iron-grey">Loading dashboard...</p>
      </div>
    );
  }

  if (!metrics?.has_data) {
    return <EmptyDashboard onRefresh={handleRefresh} isRefreshing={refreshing} />;
  }

  return (
    <div data-testid="financial-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-fraunces font-bold text-2xl text-obsidian">Financial Dashboard</h1>
          <p className="font-inter text-sm text-iron-grey mt-1">
            {metrics.latest_period ? `Latest: ${metrics.latest_period}` : 'AI-powered insights from your documents'}
          </p>
        </div>
        <Button 
          onClick={handleRefresh}
          disabled={refreshing}
          variant="outline"
          className="border-obsidian text-obsidian hover:bg-obsidian hover:text-parchment"
          data-testid="refresh-btn"
        >
          <RefreshCw size={16} strokeWidth={1.5} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Hero Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <HeroMetric 
          label="Cash Position"
          value={metrics.cash_position ? formatCurrency(metrics.cash_position, metrics.currency) : '—'}
          subtitle="Available liquidity"
          icon={DollarSign}
          trend={null}
        />
        <HeroMetric 
          label="Cash Runway"
          value={metrics.cash_runway_months ? `${metrics.cash_runway_months} Months` : '—'}
          subtitle={metrics.avg_monthly_burn ? `at ${formatCurrency(metrics.avg_monthly_burn)}/mo burn` : 'Based on average expenses'}
          icon={Clock}
          trend={null}
        />
      </div>

      {/* Stat Cards Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard 
          label="Revenue"
          value={metrics.revenue_history?.length > 0 
            ? formatCurrency(metrics.revenue_history[metrics.revenue_history.length - 1]?.value)
            : '—'}
          trend={metrics.mom_revenue_change}
          chartData={metrics.revenue_history}
          color="#3B82F6"
        />
        <StatCard 
          label="Net Profit"
          value={metrics.net_profit_history?.length > 0 
            ? formatCurrency(metrics.net_profit_history[metrics.net_profit_history.length - 1]?.value)
            : '—'}
          trend={metrics.mom_profit_change}
          chartData={metrics.net_profit_history}
          color={metrics.net_profit_history?.[metrics.net_profit_history.length - 1]?.value >= 0 ? '#10B981' : '#EF4444'}
        />
        <StatCard 
          label="Avg Monthly Expenses"
          value={metrics.avg_monthly_burn ? formatCurrency(metrics.avg_monthly_burn) : '—'}
          trend={null}
          chartData={metrics.burn_rate_history}
          color="#7F8C8D"
        />
        <StatCard 
          label="Tax Liability"
          value={metrics.tax_liability ? formatCurrency(metrics.tax_liability) : '—'}
          trend={null}
          chartData={null}
          color="#F59E0B"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <ProfitBurnChart 
          netProfitHistory={metrics.net_profit_history || []}
          burnRateHistory={metrics.burn_rate_history || []}
        />
        <TaxReserveGauge 
          liability={metrics.tax_liability}
          reserved={metrics.tax_reserved}
          percentage={metrics.tax_reserve_percentage}
        />
      </div>

      {/* Revenue Trend */}
      <RevenueTrendChart 
        revenueHistory={metrics.revenue_history || []}
        momChange={metrics.mom_revenue_change}
      />

      {/* Footer Note */}
      <div className="mt-6 p-4 bg-parchment border border-iron-grey">
        <p className="font-inter text-xs text-iron-grey text-center">
          Metrics are automatically extracted from financial documents sent by your accountant using AI analysis. 
          Last updated: {metrics.last_updated ? new Date(metrics.last_updated).toLocaleString('en-GB') : 'Never'}
        </p>
      </div>
    </div>
  );
};

export default FinancialDashboard;
